import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, CheckCircle2, Clock3, FileDown, Filter, ShieldAlert, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import type { NonConformity } from "../types";

const severityLabel: Record<string, string> = {
  leve: "Leve",
  moderada: "Moderada",
  grave: "Grave",
  gravissima: "Gravíssima"
};

const statusLabel: Record<string, string> = {
  aberta: "Aberta",
  em_tratamento: "Em tratamento",
  resolvida: "Resolvida"
};

function parseServerDate(value: string) {
  const normalized = value.includes("T") ? value : value.replace(" ", "T") + "Z";
  return new Date(normalized);
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = parseServerDate(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("pt-BR", {
        timeZone: "America/Fortaleza",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }).format(date) + " BRT";
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = value.length === 10 ? new Date(value + "T12:00:00") : parseServerDate(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("pt-BR");
}

function isOverdue(nc: NonConformity) {
  if (!nc.deadline || nc.status === "resolvida") return false;
  const end = new Date(nc.deadline + "T23:59:59");
  return end.getTime() < Date.now();
}

export function NCReportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<NonConformity[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pendentes");
  const [severityFilter, setSeverityFilter] = useState("todas");
  const [periodFilter, setPeriodFilter] = useState("todos");
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<string[]>([]);
  const [unitFilter, setUnitFilter] = useState("");

  useEffect(() => {
    api<{ ncs: NonConformity[] }>("ncs")
      .then((data) => setItems(data.ncs))
      .finally(() => setLoading(false));
  }, []);

  const availableVehicleTypes = useMemo(() => {
    const map = new Map<string, { id: string; name: string; count: number }>();

    for (const nc of items) {
      const id = nc.vehicle_type_id || nc.vehicle_type_code || nc.vehicle_type_name || "sem-tipo";
      const current = map.get(id);
      if (current) {
        current.count += 1;
      } else {
        map.set(id, {
          id,
          name: nc.vehicle_type_name || "Tipo não identificado",
          count: 1
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [items]);

  const availableUnits = useMemo(() => {
    const map = new Map<string, string>();
    for (const nc of items) {
      if (nc.unit_id && nc.unit_name) map.set(nc.unit_id, nc.unit_name);
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [items]);

  const selectedUnitName = unitFilter ? availableUnits.find((unit) => unit.id === unitFilter)?.name || "Unidade selecionada" : "";

  const selectedVehicleTypeNames = useMemo(() => {
    if (!selectedVehicleTypes.length) return [];
    const selected = new Set(selectedVehicleTypes);
    return availableVehicleTypes
      .filter((type) => selected.has(type.id))
      .map((type) => type.name);
  }, [availableVehicleTypes, selectedVehicleTypes]);

  function toggleVehicleType(typeId: string) {
    setSelectedVehicleTypes((current) =>
      current.includes(typeId)
        ? current.filter((id) => id !== typeId)
        : [...current, typeId]
    );
  }

  function clearVehicleTypeFilters() {
    setSelectedVehicleTypes([]);
  }

  const filtered = useMemo(() => {
    const now = Date.now();
    return items.filter((nc) => {
      if (statusFilter === "pendentes" && nc.status === "resolvida") return false;
      if (statusFilter !== "todas" && statusFilter !== "pendentes" && nc.status !== statusFilter) return false;
      if (severityFilter !== "todas" && nc.severity !== severityFilter) return false;

      if (selectedVehicleTypes.length) {
        const typeId = nc.vehicle_type_id || nc.vehicle_type_code || nc.vehicle_type_name || "sem-tipo";
        if (!selectedVehicleTypes.includes(typeId)) return false;
      }

      if (unitFilter && nc.unit_id !== unitFilter) return false;

      if (periodFilter !== "todos") {
        const days = Number(periodFilter);
        const created = parseServerDate(nc.created_at).getTime();
        if (!Number.isNaN(created) && created < now - days * 86400000) return false;
      }
      return true;
    });
  }, [items, statusFilter, severityFilter, periodFilter, selectedVehicleTypes, unitFilter]);

  const summary = useMemo(() => {
    const total = filtered.length;
    const open = filtered.filter((nc) => nc.status === "aberta").length;
    const treatment = filtered.filter((nc) => nc.status === "em_tratamento").length;
    const resolved = filtered.filter((nc) => nc.status === "resolvida").length;
    const overdue = filtered.filter(isOverdue).length;
    const critical = filtered.filter((nc) => nc.severity === "grave" || nc.severity === "gravissima").length;
    const gravissima = filtered.filter((nc) => nc.severity === "gravissima").length;
    const withoutResponsible = filtered.filter((nc) => nc.status !== "resolvida" && !nc.responsible).length;
    const withoutAction = filtered.filter((nc) => nc.status !== "resolvida" && !nc.corrective_action).length;

    return { total, open, treatment, resolved, overdue, critical, gravissima, withoutResponsible, withoutAction };
  }, [filtered]);

  const recommendations = useMemo(() => {
    const notes: string[] = [];
    if (summary.gravissima > 0) {
      notes.push("Priorizar imediatamente as não conformidades gravíssimas e acompanhar a execução das ações até a verificação de eficácia.");
    } else if (summary.critical > 0) {
      notes.push("Priorizar as não conformidades classificadas como graves e acompanhar seus prazos em reunião de gestão.");
    }
    if (summary.overdue > 0) {
      notes.push("Regularizar os prazos vencidos, registrando nova previsão e justificativa quando necessário.");
    }
    if (summary.withoutResponsible > 0) {
      notes.push("Definir responsável para todas as pendências sem responsável formalmente indicado.");
    }
    if (summary.withoutAction > 0) {
      notes.push("Formalizar a ação corretiva das pendências que ainda não possuem plano de ação registrado.");
    }
    if (!notes.length) {
      notes.push("Manter o acompanhamento das ações registradas e validar a eficácia antes do encerramento definitivo das não conformidades.");
    }
    return notes;
  }, [summary]);

  const scopeLabel = [
    statusFilter === "pendentes" ? "Pendentes" : statusFilter === "todas" ? "Todos os status" : statusLabel[statusFilter],
    severityFilter === "todas" ? "Todas as gravidades" : severityLabel[severityFilter],
    periodFilter === "todos" ? "Todo o histórico" : "Últimos " + periodFilter + " dias",
    selectedVehicleTypeNames.length
      ? "Tipos: " + selectedVehicleTypeNames.join(", ")
      : "Todos os tipos de veículo/equipamento",
    unitFilter ? "Unidade: " + selectedUnitName : "Todas as unidades"
  ].join(" · ");

  const generatedAt = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Fortaleza",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).format(new Date()) + " BRT";

  const codeDate = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Fortaleza",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(new Date()).replaceAll("-", "");

  if (loading) return <div className="center-page">Preparando relatório de não conformidades...</div>;

  return (
    <section className="nc-report-page">
      <div className="nc-report-toolbar no-print">
        <button className="secondary" type="button" onClick={() => navigate("/nao-conformidades")}>
          <ArrowLeft size={17} /> Voltar
        </button>

        <div className="nc-report-filters">
          <label>Status
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="pendentes">Pendentes</option>
              <option value="todas">Todos</option>
              <option value="aberta">Abertas</option>
              <option value="em_tratamento">Em tratamento</option>
              <option value="resolvida">Resolvidas</option>
            </select>
          </label>
          <label>Gravidade
            <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
              <option value="todas">Todas</option>
              <option value="leve">Leve</option>
              <option value="moderada">Moderada</option>
              <option value="grave">Grave</option>
              <option value="gravissima">Gravíssima</option>
            </select>
          </label>
          <label>Período
            <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)}>
              <option value="todos">Todo o histórico</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
              <option value="180">Últimos 180 dias</option>
              <option value="365">Últimos 12 meses</option>
            </select>
          </label>
        </div>

        <button className="primary" type="button" onClick={() => window.print()}>
          <FileDown size={17} /> Imprimir / Salvar PDF
        </button>
      </div>

      <div className="panel nc-report-type-filter no-print">
        <div className="nc-report-type-filter-heading">
          <div className="panel-title-icon">
            <Filter size={18} />
            <div>
              <span className="section-kicker">COMPOSIÇÃO DO RELATÓRIO</span>
              <h2>Tipos de veículos / equipamentos</h2>
            </div>
          </div>
          <div className="nc-report-type-count">
            <strong>{filtered.length}</strong>
            <span>NC(s) no relatório</span>
          </div>
        </div>

        <p className="muted">
          Marque quantos tipos desejar e escolha a unidade que deverá constar no relatório.
        </p>

        <label className="nc-report-unit-filter">
          <span>Unidade do relatório</span>
          <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
            <option value="">Todas as unidades</option>
            {availableUnits.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
          </select>
        </label>

        <div className="nc-report-type-options">
          {availableVehicleTypes.map((type) => {
            const checked = selectedVehicleTypes.includes(type.id);
            return (
              <label className={"nc-report-type-option " + (checked ? "selected" : "")} key={type.id}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleVehicleType(type.id)}
                />
                <span>
                  <strong>{type.name}</strong>
                  <small>{type.count} NC(s) disponível(is)</small>
                </span>
              </label>
            );
          })}
        </div>

        {selectedVehicleTypes.length > 0 && (
          <button className="secondary nc-report-clear-types" type="button" onClick={clearVehicleTypeFilters}>
            <X size={15} /> Limpar seleção ({selectedVehicleTypes.length})
          </button>
        )}
      </div>

      <article className="nc-management-report">
        <header className="nc-management-header">
          <div className="nc-report-brand">
            <img src="/mm-logo.svg" alt="MM Locações" />
            <div>
              <span>MM FLEET · QUALIDADE E SEGURANÇA</span>
              <h1>Relatório Gerencial de Não Conformidades</h1>
              <p>Acompanhamento de desvios, causas, ações corretivas, responsáveis e prazos.</p>
            </div>
          </div>

          <div className="nc-report-code">
            <span>Documento</span>
            <strong>RNC-GER-{codeDate}</strong>
            <small>Emitido em {generatedAt}</small>
          </div>
        </header>

        <section className="nc-management-meta">
          <div><span>Escopo do relatório</span><strong>{scopeLabel}</strong></div>
          <div><span>Emitido por</span><strong>{user?.name || "Usuário MM Fleet"}</strong></div>
          <div><span>Total no escopo</span><strong>{summary.total} NC(s)</strong></div>
        </section>

        <section className="nc-executive-summary">
          <div className="nc-report-section-title">
            <div>
              <span>RESUMO EXECUTIVO</span>
              <h2>Visão geral para a gerência</h2>
            </div>
          </div>

          <p>
            No escopo selecionado foram identificadas <strong>{summary.total} não conformidade(s)</strong>,
            sendo <strong>{summary.open} aberta(s)</strong>, <strong>{summary.treatment} em tratamento</strong> e
            <strong> {summary.resolved} resolvida(s)</strong>. Há <strong>{summary.critical} ocorrência(s) grave/gravíssima(s)</strong>
            e <strong> {summary.overdue} prazo(s) vencido(s)</strong>.
          </p>

          <div className="nc-report-kpis">
            <div><strong>{summary.total}</strong><span>Total</span></div>
            <div className="open"><strong>{summary.open}</strong><span>Abertas</span></div>
            <div className="treatment"><strong>{summary.treatment}</strong><span>Em tratamento</span></div>
            <div className="resolved"><strong>{summary.resolved}</strong><span>Resolvidas</span></div>
            <div className="critical"><strong>{summary.critical}</strong><span>Graves / Gravíssimas</span></div>
            <div className="overdue"><strong>{summary.overdue}</strong><span>Prazo vencido</span></div>
          </div>
        </section>

        <section className="nc-management-recommendations">
          <div className="nc-report-section-title">
            <div>
              <span>PRIORIDADES DE GESTÃO</span>
              <h2>Pontos que exigem acompanhamento</h2>
            </div>
          </div>
          <div className="nc-priority-list">
            {recommendations.map((item, index) => (
              <div key={item}>
                {index === 0 && summary.critical > 0 ? <ShieldAlert size={18} /> : <AlertTriangle size={18} />}
                <span>{item}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="nc-management-details">
          <div className="nc-report-section-title">
            <div>
              <span>PLANO DE AÇÃO</span>
              <h2>Detalhamento das não conformidades</h2>
            </div>
            <strong>{filtered.length} registro(s)</strong>
          </div>

          {!filtered.length ? (
            <div className="nc-report-empty">
              <CheckCircle2 size={24} />
              Nenhuma não conformidade encontrada para os filtros selecionados.
            </div>
          ) : (
            <div className="nc-report-items">
              {filtered.map((nc, index) => (
                <article className={"nc-report-item severity-border-" + nc.severity} key={nc.id}>
                  <div className="nc-report-item-header">
                    <div>
                      <span className="nc-report-number">NC {String(index + 1).padStart(2, "0")}</span>
                      <h3>{nc.vehicle_identifier || "Veículo / equipamento"}</h3>
                      <div className="nc-report-vehicle-meta">
                        {nc.vehicle_type_name && <span className="nc-report-vehicle-type">{nc.vehicle_type_name}</span>}
                        <span className="unit-badge">{nc.unit_name || "Unidade não definida"}</span>
                      </div>
                      <p>
                        {nc.vehicle_plate ? "Placa: " + nc.vehicle_plate + " · " : ""}
                        Inspeção: {formatDateTime(nc.inspection_created_at || nc.created_at)}
                        {nc.inspector_name ? " · Inspetor: " + nc.inspector_name : ""}
                      </p>
                    </div>
                    <div className="nc-report-badges">
                      <span className={"severity " + nc.severity}>{severityLabel[nc.severity] || nc.severity}</span>
                      <span className={"nc-status-badge " + nc.status}>{statusLabel[nc.status]}</span>
                      {isOverdue(nc) && <span className="nc-overdue-badge">Prazo vencido</span>}
                    </div>
                  </div>

                  <div className="nc-report-problem">
                    <span>PROBLEMA IDENTIFICADO</span>
                    <p>{nc.description}</p>
                  </div>

                  <div className="nc-report-action-grid">
                    <div>
                      <span>Causa identificada</span>
                      <p>{nc.root_cause || "Ainda não registrada."}</p>
                    </div>
                    <div>
                      <span>Ação corretiva</span>
                      <p>{nc.corrective_action || "Ação corretiva ainda não registrada."}</p>
                    </div>
                    <div>
                      <span>Responsável</span>
                      <p>{nc.responsible || "A definir"}</p>
                    </div>
                    <div>
                      <span>Prazo</span>
                      <p>{formatDate(nc.deadline)}</p>
                    </div>
                    <div className="full">
                      <span>Verificação / conclusão</span>
                      <p>{nc.verification_notes || (nc.status === "resolvida" ? "Conclusão não registrada." : "Pendente de verificação após execução da ação.")}</p>
                    </div>
                  </div>

                  <div className="nc-report-item-footer">
                    <span>
                      <Clock3 size={13} /> NC registrada em {formatDateTime(nc.created_at)}
                      {nc.resolved_at ? " · Resolvida em " + formatDateTime(nc.resolved_at) : ""}
                    </span>
                    <Link className="secondary no-print" to={"/inspecoes/" + nc.inspection_id + "/relatorio"}>
                      Abrir inspeção de origem
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="nc-management-signoff">
          <div>
            <span>Qualidade / SST</span>
            <strong>Responsável pelo acompanhamento</strong>
          </div>
          <div>
            <span>Gerência</span>
            <strong>Ciência e direcionamento</strong>
          </div>
        </section>

        <footer className="nc-management-footer">
          <div>MM Locações · MM Fleet</div>
          <span>Relatório gerado a partir dos registros operacionais de não conformidades do sistema.</span>
        </footer>
      </article>
    </section>
  );
}
